import React, { useState, useContext, useEffect } from "react";
import { Icon, useToast } from "@chakra-ui/react";
import { FiEdit3 } from "react-icons/fi";
import { MdCancel } from "react-icons/md";
import { AiOutlineReload } from "react-icons/ai";
import { BsCheckCircle } from "react-icons/bs";
import { arrayToOption, numberFormatter } from "../../../../Utils/formatUtils";
import { ProductsContext } from "../../../Context/ProductsContext";
import ResponsiveRow from "../../../Common/Table/ResponsiveRow";
import TdType from "../../../Common/Table/TdType";

const ProductItem = ({
  product,
  setShowNewProduct,
  initialAction = "row",
  updateProducts,
  userPriceList,
  priceList,
  setIsEdited,
}) => {
  const [action, setAction] = useState(initialAction);
  const [newProduct, setNewProduct] = useState(product);
  const [productsOptions, setProductsOptions] = useState([]);
  const { products, isLoadingProduct } = useContext(ProductsContext);
  const toast = useToast();

  useEffect(() => {
    setNewProduct(product);
    if (action === "new" || action === "edit") setIsEdited(true);
  }, [action]);

  useEffect(() => {
    if (!isLoadingProduct) {
      setProductsOptions(arrayToOption(products, "id", "name", "brand"));
    }
  }, [isLoadingProduct]);

  const validateForm = async () => {
    if (!newProduct.id) {
      toast({
        title: "Se ha producido un error.",
        description: "Ingrese un artículo.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } else if (newProduct.profit < newProduct.discount) {
      toast({
        title: "Se ha producido un error.",
        description: "La ganancia es menor al descuento.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } else {
      let product = { ...newProduct, custom: 1 };

      updateProducts(product, action);
      setShowNewProduct(false);
      setAction("row");
    }
  };

  const handleCancel = () => {
    setAction("row");
  };

  const handleSwitchChange = (val) => {
    if (newProduct.discount >= newProduct.profit) {
      toast({
        title: "Se ha producido un error.",
        description: "La ganancia es menor al descuento.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      setNewProduct({
        ...newProduct,
        active: 0,
      });
      return;
    }

    let productOriginal = priceList.find((p) => p.id === newProduct.id);
    let custom;
    if (productOriginal) {
      custom = 0;
    } else {
      custom = 1;
    }

    setNewProduct({
      ...newProduct,
      active: val.target.checked ? 1 : 0,
      custom: custom,
    });
  };

  const handleChange = (val) => {
    setNewProduct({ ...newProduct, [val.target.id]: val.target.value });
  };

  const handleEdit = () => {
    setAction("edit");
  };

  const handleSubmit = () => {
    validateForm();
  };

  const handleProductChange = (val) => {
    let productInList = userPriceList.find((prod) => prod.id == val.value);
    if (typeof productInList !== "undefined") {
      toast({
        title: "Error al cargar artículo.",
        description: "El artículo ya se encuentra en el listado.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return false;
    }

    setNewProduct({
      ...newProduct,
      name: products.find((pr) => pr.id === val.value).name,
      brand: products.find((pr) => pr.id === val.value).brand,
      id: val.value,
      discount: 0,
      profit: 0,
      active: 0,
    });
  };

  const getDiscountPrice = (newProduct) => {
    if (newProduct.id) {
      let cost = products.find((pr) => pr.id === newProduct.id).cost;
      return (
        cost * (1 + newProduct.profit / 100) -
        (cost * newProduct.discount) / 100
      ).toFixed(2);
    }
  };

  const getProfitPrice = (newProduct) => {
    if (newProduct.id) {
      let cost =
        products.find((pr) => pr.id === newProduct.id).cost *
        (1 + newProduct.profit / 100);
      return cost.toFixed(2);
    }
  };

  const setActiveStatus = () => {
    if (product.discount >= product.profit) return false;
  };

  const handleReset = (newProduct) => {
    let productOriginal = priceList.find((p) => p.id === newProduct.id);
    if (productOriginal) {
      productOriginal.custom = 0;
      updateProducts(productOriginal, "edit");
    } else {
      updateProducts(newProduct, "remove");
    }
  };

  const rowDisplay = () => {
    let rowData = [];
    // eslint-disable-next-line default-case
    switch (action) {
      case "edit":
        rowData = [
          {
            tdStyle: {
              width: "15%",
            },
            item: <TdType text={product.brand + " " + product.name} />,
          },
          {
            tdStyle: {
              width: "12%",
              px: "2",
            },
            item: (
              <TdType
                id="cost"
                text={
                  newProduct.id &&
                  numberFormatter(
                    products.find((pr) => pr.id === newProduct.id).cost
                  )
                }
              />
            ),
          },
          {
            tdStyle: {
              textAlign: "center",
              width: "12%",
              px: "2",
            },
            item: (
              <TdType
                inputType="input"
                isDisabled={!newProduct.id}
                id="profit"
                value={newProduct.profit}
                onChange={handleChange}
              />
            ),
          },
          {
            tdStyle: {
              width: "12%",
              px: "2",
            },
            item: (
              <TdType
                id="profitPrice"
                text={numberFormatter(getProfitPrice(newProduct))}
              />
            ),
          },
          {
            tdStyle: {
              textAlign: "center",
              width: "12%",
              px: "2",
            },
            item: (
              <TdType
                inputType="input"
                isDisabled={!newProduct.profit}
                id="discount"
                value={newProduct.discount}
                onChange={handleChange}
              />
            ),
          },
          {
            tdStyle: {
              width: "12%",
              px: "2",
            },
            item: (
              <TdType
                id="discountPrice"
                text={numberFormatter(getDiscountPrice(newProduct))}
              />
            ),
          },
          {
            tdStyle: {
              textAlign: "center",
            },
            item: (
              <TdType
                inputType="switch"
                id="active"
                isChecked={
                  newProduct.discount >= newProduct.profit
                    ? false
                    : newProduct.active
                }
                onChange={handleSwitchChange}
              />
            ),
          },
          {
            item: <TdType text={newProduct.custom ? "Sí" : "No"} />,
          },
          {
            tdStyle: {
              textAlign: "center",
              fontSize: "1.5em",
              width: "15%",
              px: "2",
            },
            item: (
              <>
                <Icon
                  _hover={{ cursor: "pointer" }}
                  mx="2"
                  as={BsCheckCircle}
                  color="green"
                  onClick={() => handleSubmit()}
                  title={"Aceptar"}
                ></Icon>
                <Icon
                  _hover={{ cursor: "pointer" }}
                  mx="2"
                  as={MdCancel}
                  color="red"
                  title={"Cancelar"}
                  onClick={() => handleCancel()}
                ></Icon>
              </>
            ),
          },
        ];
        return (
          <ResponsiveRow
            key={product.productId}
            cells={rowData}
          ></ResponsiveRow>
        );

      case "new":
        rowData = [
          {
            tdStyle: {
              textAlign: "center",
              width: "25%",
            },
            item: (
              <TdType
                inputType="modal"
                name={`name`}
                id={`name`}
                title={"Elija un artículo"}
                modalTitle={"Listado de artículos"}
                placeholder={"Elija un artículo"}
                options={productsOptions}
                value={productsOptions.find(
                  (item) => item.value == newProduct.id
                )}
                onChange={(val) => handleProductChange(val)}
              />
            ),
          },
          {
            tdStyle: {
              width: "12%",
              px: "2",
            },
            item: (
              <TdType
                id="cost"
                text={
                  newProduct.id &&
                  products.find((pr) => pr.id === newProduct.id).cost
                }
              />
            ),
          },
          {
            tdStyle: {
              textAlign: "center",
              width: "12%",
              px: "2",
            },
            item: (
              <TdType
                inputType="input"
                isDisabled={!newProduct.id}
                id="profit"
                value={newProduct.profit}
                onChange={handleChange}
              />
            ),
          },
          {
            tdStyle: {
              width: "12%",
              px: "2",
            },
            item: <TdType id="profitPrice" text={getProfitPrice(newProduct)} />,
          },
          {
            tdStyle: {
              textAlign: "center",
              width: "12%",
              px: "2",
            },
            item: (
              <TdType
                inputType="input"
                isDisabled={!newProduct.profit}
                id="discount"
                value={newProduct.discount}
                onChange={handleChange}
              />
            ),
          },
          {
            tdStyle: {
              width: "12%",
              px: "2",
            },
            item: (
              <TdType id="discountPrice" text={getDiscountPrice(newProduct)} />
            ),
          },
          {
            tdStyle: {
              textAlign: "center",
            },
            item: (
              <TdType
                inputType="switch"
                id="active"
                isChecked={newProduct.active}
                onChange={handleSwitchChange}
              />
            ),
          },
          {
            item: <TdType text={newProduct.custom ? "Sí" : "No"} />,
          },
          {
            tdStyle: {
              textAlign: "center",
              fontSize: "1.5em",
              width: "15%",
              px: "2",
            },
            item: (
              <>
                <Icon
                  _hover={{ cursor: "pointer" }}
                  mx="2"
                  as={BsCheckCircle}
                  color="green"
                  onClick={() => handleSubmit()}
                  title={"Aceptar"}
                ></Icon>
                <Icon
                  _hover={{ cursor: "pointer" }}
                  mx="2"
                  as={MdCancel}
                  color="red"
                  title={"Cancelar"}
                  onClick={() => setShowNewProduct(false)}
                ></Icon>
              </>
            ),
          },
        ];
        return (
          <ResponsiveRow
            key={product.productId}
            cells={rowData}
          ></ResponsiveRow>
        );

      case "row":
        rowData = [
          {
            tdStyle: {
              width: "15%",
            },
            item: <TdType text={product.brand + " " + product.name} />,
          },
          {
            item: (
              <TdType text={products.find((pr) => pr.id === product.id).cost} />
            ),
          },
          {
            item: <TdType text={product.profit} />,
          },
          {
            item: <TdType text={getProfitPrice(product)} />,
          },
          {
            item: <TdType text={product.discount} />,
          },
          {
            item: <TdType text={getDiscountPrice(product)} />,
          },
          {
            tdStyle: {
              textAlign: "center",
            },
            item: (
              <TdType
                inputType="switch"
                id="active"
                isChecked={product.active}
                isDisabled={true}
              />
            ),
          },

          {
            item: <TdType text={product.custom ? "Sí" : "No"} />,
          },

          {
            tdStyle: {
              textAlign: "center",
              fontSize: "1.5em",
              width: "15%",
              px: "2",
            },
            item: (
              <>
                <Icon
                  mx="2"
                  as={FiEdit3}
                  _hover={{ cursor: "pointer" }}
                  onClick={() => handleEdit(product)}
                  title={"Editar"}
                ></Icon>
                {product.custom ? (
                  <Icon
                    as={AiOutlineReload}
                    _hover={{ cursor: "pointer" }}
                    mx="2"
                    title="Restaurar"
                    onClick={() => handleReset(product)}
                  />
                ) : null}
              </>
            ),
          },
        ];
        return <ResponsiveRow key={product.id} cells={rowData}></ResponsiveRow>;
    }
  };

  return !isLoadingProduct && <>{rowDisplay()}</>;
};

export default ProductItem;
